"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.saveExternalCostRequestSchema = exports.externalCostSummarySchema = exports.externalCostSchema = exports.EXTERNAL_COST_LINKS = void 0;
exports.externalCostTotal = externalCostTotal;
exports.recurrenceLabelKey = recurrenceLabelKey;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Külső költségek — C56, Phase 4. What a project spends that is not time.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A RECURRING COST CHARGES ITS FULL AMOUNT TO EVERY PERIOD IT TOUCHES.** Twelve months of a
 * 180 000 Ft licence is 2 160 000 Ft, not 180 000 spread across twelve — the detail screen says so
 * in words, and `PHASE-4.md` records it because the opposite reading is the intuitive one. A line
 * item's budget two tables away divides; this multiplies, and the two look identical in a schema.
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** The mockup's own `link.kind`, and all three are ordinary. */
exports.EXTERNAL_COST_LINKS = ['project', 'line_item', 'occurrence'];
exports.externalCostSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `ec_7c2d9e01` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('externalCost'),
    /** The quotable number — `EK-3041`. */
    number: zod_1.z.number().int(),
    projectId: zod_1.z.string(),
    projectName: zod_1.z.string(),
    projectColor: zod_1.z.string().nullable(),
    name: zod_1.z.string(),
    incurredOn: wireDateSchema,
    /** ⚠️ A string, like every other `numeric` on the wire. Null without `moneyVisible`. */
    amount: zod_1.z.string().nullable(),
    currencyCode: zod_1.z.string().nullable(),
    /**
     * 22 Oct 2026 · **whether this row's money may be shown** — `project.finance` for its project
     * (`project-reach.ts`). False: every amount and currency on it is null and the screen hides
     * those fields rather than drawing a blank.
     */
    moneyVisible: zod_1.z.boolean(),
    /**
     * 22 Oct 2026 · whether the caller may change it — `contract.manage`, at `own` only on a project
     * they are PM of (`project-manage.ts`). The screen offers no edit the server would refuse.
     */
    canManage: zod_1.z.boolean(),
    linkKind: zod_1.z.enum(exports.EXTERNAL_COST_LINKS),
    lineItemId: zod_1.z.string().nullable(),
    lineItemName: zod_1.z.string().nullable(),
    /** C62 · ⚠️ A line item's ADDRESS is its contract's code and this ordinal — neither alone. */
    lineItemNumber: zod_1.z.number().int().nullable(),
    contractId: zod_1.z.string().nullable(),
    contractCode: zod_1.z.string().nullable(),
    /** R-57 · the line item's and contract's public identifiers — the address the screens link to. */
    lineItemIdentifier: zod_1.z.string().nullable(),
    contractIdentifier: zod_1.z.string().nullable(),
    occurrenceStartsOn: wireDateSchema.nullable(),
    /** Null for a one-off. */
    recurrenceUnit: zod_1.z.enum(['week', 'month']).nullable(),
    recurrenceStep: zod_1.z.number().int().nullable(),
    recurrenceUntil: wireDateSchema.nullable(),
    ownerMemberId: zod_1.z.string(),
    ownerName: zod_1.z.string(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from.** The normalised email,
     * produced by `wireAvatarSeed`. This shape carries no avatar URL, which is why the first
     * pass missed it — it went looking for pictures and this row only ever had initials.
     */
    ownerAvatarSeed: zod_1.z.string().nullable(),
    /** 28 Oct 2026 · their uploaded picture's path, when they have one — drawn over the seed's face. */
    ownerAvatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **HOW MANY PERIODS IT LANDS ON, AND THE TOTAL THAT FOLLOWS.** Computed server-side because
     * it needs the linked contract's occurrence series — which the client does not hold and should
     * not have to generate to print a figure. `1` and `amount` for a one-off, so a caller summing a
     * list never branches.
     */
    periods: zod_1.z.number().int(),
    total: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ 20 Oct 2026 · FX-3 · **`total` in one currency, EACH CHARGE AT ITS OWN DAY'S RATE** —
     * what a list footer sums. Converted on the server because the day rates live there: a screen
     * multiplying `total` by today's rate re-prices last March's licence every time the euro moves.
     * The currency is the line item's own when the list is one line item's (its money card sums in
     * it), else the workspace's default. A pair with no rate passes at 1:1, as everywhere else.
     */
    converted: zod_1.z.object({ currencyCode: zod_1.z.string(), total: zod_1.z.string() }).nullable(),
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ C62 · WHICH periods, not just how many — the detail's *Érintett időszakok*.│
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **EMPTY IS NOT THE SAME AS `periods: 1`, AND BOTH ARE HONEST.** A one-off cost, and a
     * recurring one on a project with no recurring contract behind it, land on no contract period at
     * all — they are still charged once, which is what `periods` says. So the count stays the
     * charging multiplier and this stays the answer to *which*; deriving either from the other would
     * make a one-off claim a period it does not have, or make its total vanish.
     *
     * ⚠️ **On the LIST row as well as the detail, because the server already has them.** The service
     * generates the contract's series to count the hits; handing back the hits it counted costs one
     * array and removes a second endpoint that would have to agree with this one.
     */
    occurrences: zod_1.z.array(zod_1.z.object({
        /** 1-based, and it is what the row is labelled `#3` — the contract's own index. */
        index: zod_1.z.number().int(),
        from: wireDateSchema,
        to: wireDateSchema,
        /** The calendar decides, never a column — `occurrenceState`'s three. */
        state: zod_1.z.enum(['planned', 'active', 'closed']),
    })),
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    /** C62 · when it was booked — the detail's *Rögzítve* row. An instant, not a calendar date. */
    createdAt: zod_1.z.string(),
    version: zod_1.z.number().int(),
});
/**
 * 31 Oct 2026 · ⚠️ RULE 8 — `GET /external-costs/summary`: the cost list's footer, over the whole
 * filtered set now that the list pages. `total` is every cost's whole-term figure converted into
 * `currencyCode`, each charge at its own day's rate (FX-3) — null, with `moneyVisible` false, for a
 * caller without the project's money.
 */
exports.externalCostSummarySchema = zod_1.z.object({
    count: zod_1.z.number().int(),
    moneyVisible: zod_1.z.boolean(),
    currencyCode: zod_1.z.string().nullable(),
    total: zod_1.z.string().nullable(),
});
/**
 * ⚠️ **The link arms are a discriminated union here too**, so a request cannot name a line item and
 * an occurrence at once — the same shape `0132`'s check constraint enforces, expressed where a bad
 * request is refused with a field name rather than a constraint violation.
 */
const linkSchema = zod_1.z.discriminatedUnion('linkKind', [
    zod_1.z.object({ linkKind: zod_1.z.literal('project') }),
    zod_1.z.object({ linkKind: zod_1.z.literal('line_item'), lineItemId: zod_1.z.string() }),
    zod_1.z.object({
        linkKind: zod_1.z.literal('occurrence'),
        contractId: zod_1.z.string(),
        occurrenceStartsOn: wireDateSchema,
    }),
]);
exports.saveExternalCostRequestSchema = zod_1.z.intersection(zod_1.z.object({
    projectId: zod_1.z.string(),
    name: zod_1.z.string().trim().min(1).max(200),
    incurredOn: wireDateSchema,
    amount: zod_1.z.number().min(0),
    currencyCode: zod_1.z.string().regex(/^[A-Z]{3}$/),
    recurrenceUnit: zod_1.z.enum(['week', 'month']).nullable().optional(),
    recurrenceStep: zod_1.z.number().int().min(1).max(52).nullable().optional(),
    recurrenceUntil: wireDateSchema.nullable().optional(),
    ownerMemberId: zod_1.z.string(),
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    version: zod_1.z.number().int().optional(),
}), linkSchema);
/**
 * What a recurring cost comes to in total.
 *
 * ⚠️ **Multiplied, never divided** — see the header. `periods` is how many the linked contract has
 * inside the recurrence's own window, and it is `1` where there is no recurrence at all, so this is
 * safe to call on every row of a list.
 */
function externalCostTotal(amount, periods) {
    return amount * Math.max(1, periods);
}
/**
 * ⚠️ **The recurrence in words, and the step is what makes it three cases rather than two.**
 * *Havonta*, *Hetente*, *Minden 3. hónap* — the screen's own labels come from the catalogue, so
 * this answers which key to use rather than the text itself (rule 7).
 */
function recurrenceLabelKey(unit, step) {
    if (unit === null)
        return 'once';
    const every = Math.max(1, step ?? 1);
    if (every === 1)
        return unit === 'week' ? 'weekly' : 'monthly';
    return unit === 'week' ? 'everyNWeeks' : 'everyNMonths';
}
//# sourceMappingURL=external-cost.js.map